import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CurrencyInput } from '@components/core-ui/form-elements/CurrencyInput';
import { Select } from '@components/core-ui/form-elements/Select';
import { Textarea } from '@components/core-ui/form-elements/Textarea';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { AdminService, AdminTransactionBodyDto, PrismaTransactionCodeDto, TransactionCodeService } from 'app/API';
import { ETRANSACTION_CHARGE_CODES } from 'app/types/enums/ETRANSACTION_CHARGE_CODES';
import React, { useEffect, useState } from 'react';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { Content } from './styles/Content';

type TProps = {
  billingAccountId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export const AddAdminTransactionModal = ({ billingAccountId, onCancel, onSuccess }: TProps) => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const [amount, setAmount] = useState(10);
  const [code, setCode] = useState<AdminTransactionBodyDto['code']>(ETRANSACTION_CHARGE_CODES.ADMIN_TRAINING);
  const [codeDetails, setCodeDetails] = useState<PrismaTransactionCodeDto>();
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<{ error?: string; message?: string } | null>(null);

  const options = [
    {
      value: ETRANSACTION_CHARGE_CODES.ADMIN_TRAINING,
      label: 'Admin Training',
    },
    {
      value: ETRANSACTION_CHARGE_CODES.ADMIN_CREDIT_ADJUSTMENT,
      label: 'Admin Credit Adjustment',
    },
    {
      value: ETRANSACTION_CHARGE_CODES.ADMIN_FREE_CREDIT_ADJUSTMENT,
      label: 'Admin Free Credit Adjustment',
    },
    {
      value: ETRANSACTION_CHARGE_CODES.ADD_FUNDS_MANUAL,
      label: 'Add Funds Manual',
    },
    {
      value: ETRANSACTION_CHARGE_CODES.ADMIN_REFUND,
      label: 'Admin Refund',
    },
  ];

  useEffect(() => {
    const getTransactionCodeDescription = async () => {
      const result = await call(TransactionCodeService.findOne({ code }));
      if (result) {
        setCodeDetails(result);
      }
    };
    getTransactionCodeDescription();
  }, [code]);

  const onSubmit = async () => {
    setError(null);
    setIsLoading(true);
    const result = await call(
      AdminService.addAdminTransaction({ baId: billingAccountId, requestBody: { amount: amount * 100, code, note } }),
    );
    setIsLoading(false);
    if (!result?.error) {
      closeModal();
      onSuccess();
    } else {
      setError({ error: result?.error, message: result?.message });
    }
  };

  const onNotesInputChange = async (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = evt.target;
    await setNote(value);
  };

  const isSubmitDisabled = isLoading || amount === 0;

  return (
    <ModalWrapper width="400px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Create an Admin Transaction</ModalTitle>
      <Content>
        <div className="input-wrapper">
          <div className="label">Transaction Type</div>
          <Select
            value={code}
            options={options}
            onChange={(_, value) => setCode(value as AdminTransactionBodyDto['code'])}
            label="Transaction Type"
            required
          />
        </div>

        <div className="py-8">
          <small>Code: {codeDetails?.code}</small>
          <p>
            <small>Name: {codeDetails?.name}</small>
          </p>

          <p>
            <small>{codeDetails?.description}</small>
          </p>
        </div>

        <div className="input-wrapper">
          <div className="label">Amount (USD)</div>

          <div className="flex flex-row">
            <CurrencyInput
              onChange={(v) => {
                setError(null);
                if (!v) {
                  setAmount(0);
                  return;
                }
                setAmount(v);
              }}
              label="Amount"
              value={amount}
            />
          </div>
        </div>

        <div className="input-wrapper">
          <div className="label">Notes</div>

          <Textarea
            onChange={onNotesInputChange}
            className="textarea"
            name="note"
            label="Transaction notes and description"
            value={note}
          />
        </div>

        {error ? (
          <div className="error">
            <p>{error.message ?? error.error ?? 'An error occurred'}</p>
          </div>
        ) : null}
        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={isSubmitDisabled} onClick={onSubmit}>
            Create transaction
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
